/**
 * The **workspace home**'s `···` — a different menu from the sidebar pane's (P-039).
 *
 * Measured on monday, `[c:ws-home-menu]` (sandbox) and `[c:main-ws-menu]` (the account's Main
 * workspace, read-only): **220 px** wide, 32 px rows, one 17 px divider —
 *
 *   Rename workspace · Change type ▸
 *   ─
 *   Leave workspace · Delete workspace
 *
 * The sidebar's nine-entry, 240 px menu is `WorkspaceMenu`; the two are not the same list and
 * were never meant to be.
 *
 * **How monday refuses an entry here**: not `aria-disabled`, not hidden — the label colour goes
 * to `rgba(50,51,56,0.38)`. Photographed side by side, the account's Main (`is_default`)
 * workspace greys **Change type · Leave workspace · Delete workspace**, while a non-default
 * workspace greys only the first two (this account's plan refuses `closed`, and the single
 * owner of a workspace cannot leave it). So the main workspace cannot be deleted, left, or
 * retyped — and `services/workspaces.ts` refuses all three whatever this menu renders.
 */
import { useConfirm, useToast } from '@elizmonday/ui';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Menu, type MenuEntry } from '../board-view/table/menus/Menu';
import { NameDialog } from './NameDialog';
import { useWorkspace, useWorkspaceMutations } from './workspaces';

/** `[c:ws-home-menu]`: the home menu is 220, the sidebar's is 240. */
export const WORKSPACE_HOME_MENU_WIDTH = 220;

const MAIN_WORKSPACE_REASON = 'The main workspace cannot be deleted, left or retyped.';

export function WorkspaceHomeMenu({
  workspaceId,
  viewerId,
  viewerIsMember,
  x,
  y,
  onClose,
}: {
  workspaceId: string;
  /** The actor, so *Leave* can remove their own membership — monday's own "leave" is that. */
  viewerId: string | null;
  viewerIsMember: boolean;
  x: number;
  y: number;
  onClose(): void;
}) {
  const workspace = useWorkspace(workspaceId);
  const mutations = useWorkspaceMutations();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [renaming, setRenaming] = useState(false);
  /** `Menu` closes itself after a plain entry, and this component owns the menu (see `AddMenu`). */
  const chose = useRef(false);

  const name = workspace.data?.name ?? 'this workspace';
  const isDefault = workspace.data?.isDefault ?? false;
  const kind = workspace.data?.kind ?? 'open';

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${name}?`,
      body: 'Every board inside it goes to the trash with it. Both are restorable for 30 days.',
      confirmLabel: 'Delete workspace',
      danger: true,
    });
    if (!ok) return;
    await mutations.remove.mutateAsync(workspaceId);
    toast(`${name} deleted`, { kind: 'positive' });
    navigate('/');
  };

  const leave = async () => {
    if (viewerId === null) return;
    const ok = await confirm({
      title: `Leave ${name}?`,
      body: 'You keep the boards you are subscribed to; the workspace stops appearing in your switcher.',
      confirmLabel: 'Leave workspace',
      danger: true,
    });
    if (!ok) return;
    await mutations.removeMembers.mutateAsync({ id: workspaceId, userIds: [viewerId] });
    toast(`You left ${name}`, { kind: 'positive' });
    navigate('/');
  };

  const changeKind = (next: 'open' | 'closed') => () => {
    mutations.update.mutate({ id: workspaceId, kind: next });
    toast(next === 'open' ? `${name} is open to everyone` : `${name} is closed to its members`, {
      kind: 'positive',
    });
  };

  if (renaming) {
    return (
      <NameDialog
        title="Rename workspace"
        label="Workspace name"
        initial={workspace.data?.name ?? ''}
        confirmLabel="Rename"
        testId="rename-workspace-dialog"
        onCancel={onClose}
        onSubmit={(next) => {
          void mutations.update.mutateAsync({ id: workspaceId, name: next }).then(onClose);
        }}
      />
    );
  }

  const entries: MenuEntry[] = [
    {
      id: 'rename',
      label: 'Rename workspace',
      'data-testid': 'workspace-menu-rename',
      onSelect: () => {
        chose.current = true;
        setRenaming(true);
      },
    },
    {
      id: 'kind',
      label: 'Change type',
      'data-testid': 'workspace-menu-kind',
      disabled: isDefault,
      ...(isDefault ? { title: MAIN_WORKSPACE_REASON } : {}),
      submenu: [
        {
          id: 'kind-open',
          label: 'Open',
          hint: kind === 'open' ? '✓' : undefined,
          disabled: isDefault || kind === 'open',
          ...(isDefault || kind === 'open' ? {} : { onSelect: changeKind('open') }),
        },
        {
          id: 'kind-closed',
          label: 'Closed',
          hint: kind === 'closed' ? '✓' : undefined,
          disabled: isDefault || kind === 'closed',
          ...(isDefault || kind === 'closed' ? {} : { onSelect: changeKind('closed') }),
        },
      ],
    },
    { id: 'd1', label: '', divider: true },
    {
      id: 'leave',
      label: 'Leave workspace',
      'data-testid': 'workspace-menu-leave',
      disabled: isDefault || !viewerIsMember || viewerId === null,
      title: isDefault
        ? MAIN_WORKSPACE_REASON
        : viewerIsMember
          ? undefined
          : 'You are not a member of this workspace.',
      ...(isDefault || !viewerIsMember || viewerId === null ? {} : { onSelect: () => void leave() }),
    },
    {
      id: 'delete',
      label: 'Delete workspace',
      'data-testid': 'workspace-menu-delete',
      danger: true,
      disabled: isDefault,
      ...(isDefault ? { title: MAIN_WORKSPACE_REASON } : { onSelect: () => void remove() }),
    },
  ];

  return (
    <Menu
      entries={entries}
      x={x}
      y={y}
      width={WORKSPACE_HOME_MENU_WIDTH}
      label="Workspace options"
      onClose={() => {
        if (!chose.current) onClose();
      }}
    />
  );
}
